import React, { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { useVacationDpEditMode } from '@/features/Collaborators/hooks/useVacationDpEditMode';

import {
    BasicInfoTab,
    BankingDataTab,
    CapabilitiesTab,
    DocumentsTab,
    EducationTab,
    FamilyTab,
    FeedbackTab,
    HealthTab,
    HiringTab,
    MarketingTab,
    RemunerationTab,
    VacationTab
} from '@/features/Collaborators/Edit/Form/Tabs';
import { getVacationTabLabel } from './fullFormTabs.definitions';

interface FullFormTabsProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    form: UseFormReturn<CollaboratorFormData>;
    selectedTab: string;
    setSelectedTab: (tab: string) => void;
}

const FullFormTabs: React.FC<FullFormTabsProps> = ({
    context,
    collaboratorId,
    form,
    selectedTab,
    setSelectedTab
}) => {
    const { control, setValue, watch, formState: { errors, isSubmitting } } = form;
    const collaboratorType = watch('collaboratorType');
    const { isVacationDpOnly } = useVacationDpEditMode();

    const allTabs = [
        { id: 'basic-info', label: 'Dados Básicos' },
        { id: 'documents', label: 'Documentos' },
        { id: 'hiring', label: 'Contratação' },
        { id: 'banking', label: 'Contas' },
        { id: 'family', label: 'Família' },
        { id: 'health', label: 'Saúde' },
        { id: 'remuneration', label: 'Remuneração' },
        { id: 'vacation', label: getVacationTabLabel(collaboratorType) },
        { id: 'capabilities', label: 'Habilidades' },
        { id: 'education', label: 'Formação' },
        { id: 'feedback', label: 'Feedbacks' },
        { id: 'marketing', label: 'Marketing' }
    ];

    const tabs = useMemo(() => {
        if (isVacationDpOnly) {
            return allTabs.filter((tab) => ['hiring', 'vacation'].includes(tab.id));
        }
        return allTabs;
    }, [isVacationDpOnly]);

    const renderTabContent = () => {
        switch (selectedTab) {
            case 'basic-info':
                return (
                    <BasicInfoTab
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 'banking':
                return (
                    <BankingDataTab
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 'capabilities':
                return (
                    <CapabilitiesTab
                        context={context}
                    />
                );
            case 'documents':
                return (
                    <DocumentsTab
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 'family':
                return (
                    <FamilyTab
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 'feedback':
                return (
                    <FeedbackTab
                        context={context}
                    />
                );
            case 'health':
                return (
                    <HealthTab
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 'hiring':
                return (
                    <fieldset
                        disabled={isVacationDpOnly}
                        className={isVacationDpOnly ? 'contents opacity-60' : 'contents'}
                    >
                        {isVacationDpOnly && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                                <p className="text-sm text-yellow-800">Esta aba é somente leitura.</p>
                            </div>
                        )}
                        <HiringTab
                            context={context}
                            collaboratorId={collaboratorId}
                            control={control}
                            setValue={setValue}
                            watch={watch}
                            errors={errors}
                            isSubmitting={isSubmitting}
                        />
                    </fieldset>
                );
            case 'education':
                return (
                    <EducationTab
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 'marketing':
                return (
                    <MarketingTab
                        context={context}
                    />
                );
            case 'remuneration':
                return (
                    <RemunerationTab
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 'vacation':
                return (
                    <VacationTab
                        context={context}
                        collaboratorId={collaboratorId}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="space-y-6">
            {/* Tabs Navigation */}
            <div className="border-b border-gray-200">
                <nav className="flex space-x-4 overflow-x-auto">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setSelectedTab(tab.id)}
                            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${selectedTab === tab.id
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
                {renderTabContent()}
            </div>
        </div>
    );
};

export default FullFormTabs; 